/**
 * IndexedDB persistence for voice recording blobs.
 *
 * After recording stops the assembled Blob is written here so that it survives
 * network failures, tab crashes, and transient API errors.  On successful
 * transcription the entry is deleted.  A TTL-based eviction pass runs on every
 * write to avoid unbounded growth.
 */

const VOICE_BLOB_DB_NAME = "cord-voice-blobs"
const VOICE_BLOB_DB_VERSION = 1
const VOICE_BLOB_STORE = "blobs"

/** Keep stale entries for at most 1 hour — these are ephemeral recordings. */
const VOICE_BLOB_TTL_MS = 60 * 60 * 1000

export interface PersistedVoiceBlob {
  /** Unique key — typically `${sessionId}:${timestamp}`. */
  key: string
  sessionId: string
  blob: Blob
  mime: string
  createdAt: number
  /** R2 object key if the upload already succeeded (avoids re-upload on retry). */
  r2Key?: string
}

// ---------------------------------------------------------------------------
// Internal helpers (follow the same IndexedDB wrapper pattern as other local caches)
// ---------------------------------------------------------------------------

function hasIndexedDb(): boolean {
  return typeof window !== "undefined" && typeof window.indexedDB !== "undefined"
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
  })
}

function transactionToPromise(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"))
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"))
  })
}

async function openVoiceBlobDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) return null
  return await new Promise((resolve, reject) => {
    const req = window.indexedDB.open(VOICE_BLOB_DB_NAME, VOICE_BLOB_DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(VOICE_BLOB_STORE)) {
        const store = db.createObjectStore(VOICE_BLOB_STORE, { keyPath: "key" })
        store.createIndex("sessionId", "sessionId", { unique: false })
        store.createIndex("createdAt", "createdAt", { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error("Failed to open voice blob DB"))
  })
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildVoiceBlobKey(sessionId: string): string {
  return `${sessionId}:${Date.now()}`
}

/** Persist a recorded blob. Returns the key used. */
export async function saveVoiceBlob(entry: PersistedVoiceBlob): Promise<void> {
  const db = await openVoiceBlobDb()
  if (!db) return

  try {
    const tx = db.transaction(VOICE_BLOB_STORE, "readwrite")
    const store = tx.objectStore(VOICE_BLOB_STORE)
    store.put(entry)
    await transactionToPromise(tx)
  } finally {
    db.close()
  }

  // Fire-and-forget eviction of stale entries.
  void evictStaleEntries()
}

/** Update the R2 key on an existing entry (after successful upload). */
export async function updateVoiceBlobR2Key(key: string, r2Key: string): Promise<void> {
  const db = await openVoiceBlobDb()
  if (!db) return

  try {
    const readTx = db.transaction(VOICE_BLOB_STORE, "readonly")
    const readStore = readTx.objectStore(VOICE_BLOB_STORE)
    const row = await requestToPromise(readStore.get(key))
    await transactionToPromise(readTx)
    if (!row) return

    const writeTx = db.transaction(VOICE_BLOB_STORE, "readwrite")
    const writeStore = writeTx.objectStore(VOICE_BLOB_STORE)
    writeStore.put({ ...row, r2Key })
    await transactionToPromise(writeTx)
  } finally {
    db.close()
  }
}

/** Load a persisted blob entry by key. Returns null if not found or expired. */
export async function loadVoiceBlob(key: string): Promise<PersistedVoiceBlob | null> {
  const db = await openVoiceBlobDb()
  if (!db) return null

  try {
    const tx = db.transaction(VOICE_BLOB_STORE, "readonly")
    const store = tx.objectStore(VOICE_BLOB_STORE)
    const row = await requestToPromise(store.get(key))
    await transactionToPromise(tx)

    if (!row || typeof row !== "object") return null
    const entry = row as PersistedVoiceBlob
    if (!entry.blob || !entry.key) return null
    if (entry.createdAt + VOICE_BLOB_TTL_MS < Date.now()) return null

    return entry
  } finally {
    db.close()
  }
}

/** Delete a persisted blob (after successful transcription or manual dismiss). */
export async function deleteVoiceBlob(key: string): Promise<void> {
  const db = await openVoiceBlobDb()
  if (!db) return

  try {
    const tx = db.transaction(VOICE_BLOB_STORE, "readwrite")
    const store = tx.objectStore(VOICE_BLOB_STORE)
    store.delete(key)
    await transactionToPromise(tx)
  } finally {
    db.close()
  }
}

/**
 * Find the most recent non-expired blob for a given session.
 *
 * Used on mount to detect blobs that were persisted before a page refresh or
 * thread navigation so the error + retry UI can be restored.
 */
export async function loadPendingVoiceBlobForSession(
  sessionId: string,
): Promise<PersistedVoiceBlob | null> {
  const db = await openVoiceBlobDb()
  if (!db) return null

  try {
    const tx = db.transaction(VOICE_BLOB_STORE, "readonly")
    const store = tx.objectStore(VOICE_BLOB_STORE)
    const index = store.index("sessionId")
    const all = await requestToPromise(index.getAll(sessionId))
    await transactionToPromise(tx)

    const now = Date.now()
    let best: PersistedVoiceBlob | null = null
    for (const row of all) {
      const entry = row as PersistedVoiceBlob
      if (!entry.blob || !entry.key) continue
      if (entry.createdAt + VOICE_BLOB_TTL_MS < now) continue
      if (!best || entry.createdAt > best.createdAt) {
        best = entry
      }
    }
    return best
  } finally {
    db.close()
  }
}

/** Remove entries older than TTL. */
async function evictStaleEntries(): Promise<void> {
  const db = await openVoiceBlobDb()
  if (!db) return

  try {
    const readTx = db.transaction(VOICE_BLOB_STORE, "readonly")
    const readStore = readTx.objectStore(VOICE_BLOB_STORE)
    const all = await requestToPromise(readStore.getAll())
    await transactionToPromise(readTx)

    const now = Date.now()
    const keysToDelete: string[] = []
    for (const row of all) {
      const entry = row as PersistedVoiceBlob
      if (entry.key && entry.createdAt + VOICE_BLOB_TTL_MS < now) {
        keysToDelete.push(entry.key)
      }
    }

    if (keysToDelete.length === 0) return

    const writeTx = db.transaction(VOICE_BLOB_STORE, "readwrite")
    const writeStore = writeTx.objectStore(VOICE_BLOB_STORE)
    for (const key of keysToDelete) {
      writeStore.delete(key)
    }
    await transactionToPromise(writeTx)
  } finally {
    db.close()
  }
}
